import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Ban, ExternalLink } from 'lucide-react';
import type { InvestigationResponse, OpsBriefBlock, ProfitDelegationPayload } from '../../types/investigator';
import type { ProfitAgentDrilldown, ProfitAgentQueryResponse } from '../../types/profitabilityAgent';
import { isCustomerProfitabilityQuestion } from '../../utils/profitDrilldownNavigation';
import { Button } from '@/components/ui/button';
import ProfitAnswerCard from '../profitAgent/ProfitAnswerCard';
import OpsBriefPanel from './OpsBriefPanel';
import { humanizeInvestigationDateTimeText } from '../../utils/humanizeInvestigationDateTimeText';

/** Reads the user's `prefers-reduced-motion` preference and reacts to changes. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduced(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduced;
}

/** Simulated streaming: reveal `text` one character at a time. Backend returns
 *  the full response in one shot, so this is a client-side typewriter that
 *  gives the user the "Zoe is generating" feel and lets us defer rendering of
 *  structural panels (evidence, timeline) until the prose has settled. */
function useTypewriter(
  text: string,
  opts: { charDelayMs?: number; enabled?: boolean } = {},
): { displayText: string; isComplete: boolean } {
  const { charDelayMs = 14, enabled = true } = opts;
  const [displayText, setDisplayText] = useState<string>(() => (enabled ? '' : text));
  const [isComplete, setIsComplete] = useState<boolean>(() => !enabled || !text);

  useEffect(() => {
    if (!enabled || !text) {
      setDisplayText(text || '');
      setIsComplete(true);
      return;
    }

    setDisplayText('');
    setIsComplete(false);

    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setDisplayText(text.slice(0, i));
      if (i >= text.length) {
        window.clearInterval(id);
        setIsComplete(true);
      }
    }, charDelayMs);

    return () => window.clearInterval(id);
  }, [text, charDelayMs, enabled]);

  return { displayText, isComplete };
}

const LABEL = 'text-sm font-medium text-muted-foreground mb-1';

/** API may return a string[] for detailed_explanation in edge cases; normalise for parsing. */
function coerceInvestigatorMultilineField(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map((x) => String(x ?? '').trim())
      .filter((s) => s.length > 0)
      .join('\n');
  }
  return '';
}

/** Turn LLM detailed_explanation into bullet strings for the UI. */
function splitDetailedExplanationToBullets(raw: string): string[] {
  const t = String(raw || '')
    .replace(/\r\n/g, '\n')
    .trim();
  if (!t) return [];

  /** Strip list marker; hyphen lists require a space after "-" so we do not eat minus-sign prose. */
  const stripBulletLead = (s: string) =>
    s
      .trim()
      .replace(/^[*•]\s+/, '')
      .replace(/^-\s+/, '')
      .replace(/^\d+\.\s*/, '')
      .trim();

  const lines = t.split(/\n/).map((l) => l.trim()).filter(Boolean);

  // One physical line may contain several Markdown bullets: "- A - B - C"
  if (lines.length === 1 && /^[-*•]/.test(lines[0]) && /\s+-\s+/.test(lines[0])) {
    const chunks = lines[0].split(/\s+-\s+/);
    const out = chunks.map((c, i) => (i === 0 ? stripBulletLead(c) : c.trim())).filter(Boolean);
    if (out.length > 1) return out;
  }

  if (lines.length > 1) {
    const bullets = lines.map(stripBulletLead).filter(Boolean);
    if (bullets.length > 0) return bullets;
    // Model emitted only markers / whitespace lines — keep readable text
    const loosened = lines
      .map((l) => l.replace(/^[*•]\s+/, '').replace(/^-\s+/, '').trim())
      .filter((l) => l.length > 0 && l !== '-' && l !== '—');
    if (loosened.length > 0) return loosened;
    return [t];
  }

  const single = lines[0] ?? t;

  if (/\s+;\s+/.test(single)) {
    const parts = single.split(/\s+;\s+/).map(stripBulletLead).filter(Boolean);
    if (parts.length > 1) return parts;
  }

  if (single.length >= 120) {
    const sentences = single
      .split(/(?<=[.!?])\s+(?=[A-Z0-9"'([])/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (sentences.length > 1) return sentences;
  }

  const one = stripBulletLead(single);
  return [one.length > 0 ? one : single];
}

/** Lower-cased phrases that the LLM uses as section headings inside
 *  detailed_explanation. Anything matching one of these on a line of its
 *  own is rendered as a section label, never as bullet content. */
const KNOWN_SECTION_HEADINGS = new Set<string>([
  'summary', 'conclusion', 'overview', 'tldr', 'tl;dr',
  "what's missing", 'whats missing', 'missing', 'gaps', 'gap', 'data gaps', 'data issues',
  "what's wrong", 'what is wrong', 'issues', 'errors', 'problems',
  'recommendations', 'recommendation', 'next steps', 'action items', 'actions',
  'evidence', 'sources', 'data sources', 'references',
  'confidence', 'reasoning', 'rationale',
  'what happened', 'details', 'analysis', 'observations', 'findings',
  'background', 'context',
]);

/** Headings that duplicate the top "summary" line — drop them entirely so
 *  we never repeat the headline answer inside "What happened". */
const REDUNDANT_SUMMARY_HEADINGS = new Set<string>([
  'summary', 'conclusion', 'overview', 'tldr', 'tl;dr',
]);

interface ExplanationBlock { heading?: string; bullets: string[] }

function normalizeHeading(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Returns the cleaned heading text if the line is a section heading, else undefined. */
function detectHeading(rawLine: string): string | undefined {
  const isMdHeading = /^#{1,6}\s+/.test(rawLine);
  const stripped = rawLine
    .replace(/^#{1,6}\s+/, '')
    .replace(/^[*•]\s+/, '')
    .replace(/^-\s+/, '')
    .replace(/^\d+\.\s*/, '')
    .replace(/\*\*/g, '')
    .trim();
  const noColon = stripped.replace(/[:：]+\s*$/, '').trim();
  if (!noColon) return undefined;
  if (KNOWN_SECTION_HEADINGS.has(normalizeHeading(noColon))) return noColon;
  if (isMdHeading && noColon.length <= 60) return noColon;
  return undefined;
}

/** Parse detailed_explanation into [{ heading?, bullets[] }] blocks.
 *  Re-uses splitDetailedExplanationToBullets for splitting each block's
 *  body so single-sentence-per-block content is still split sensibly. */
function parseExplanationBlocks(raw: string): ExplanationBlock[] {
  const t = String(raw || '').replace(/\r\n/g, '\n').trim();
  if (!t) return [];
  const lines = t.split('\n').map((l) => l.trim()).filter(Boolean);

  const groups: { heading?: string; body: string[] }[] = [];
  let current: { heading?: string; body: string[] } = { body: [] };
  for (const line of lines) {
    const heading = detectHeading(line);
    if (heading) {
      if (current.body.length > 0 || current.heading) groups.push(current);
      current = { heading, body: [] };
    } else {
      current.body.push(line);
    }
  }
  if (current.body.length > 0 || current.heading) groups.push(current);

  return groups
    .map((g) => ({ heading: g.heading, bullets: splitDetailedExplanationToBullets(g.body.join('\n')) }))
    .filter((b) => b.heading || b.bullets.length > 0);
}

/** Strip debug-style framing from the LLM summary so the headline reads as a
 *  human answer, not a transcript of the request. Drops the leading
 *  "Question: …" prefix, the trailing claims/evidence count, and the
 *  "Confidence: …" segment (already rendered separately in the chip).
 *  Converts "Conclusion: data_issue" → "Data issue.". */
function cleanSummaryText(s: string): string {
  if (!s) return s;
  let t = s.trim();
  t = t.replace(/^Question:\s*[^.!?]*[.!?]+\s*\.?\s*/i, '');
  t = t.replace(/\bConfidence:\s*[\w-]+\.?\s*/i, '');
  t = t.replace(/\b\d+\s+claim\(s\)\s+gathered\s+across\s+\d+\s+evidence\s+reference\(s\)\.?\s*/i, '');
  t = t.replace(/\bConclusion:\s*([\w-]+)\.?\s*/i, (_m, word: string) => {
    const human = String(word)
      .split(/[_-]/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(' ');
    return human ? `${human}. ` : '';
  });
  return t.replace(/\s{2,}/g, ' ').trim();
}

/** Operationally salient nouns (absence / risk) — informational amber, not alarm red. */
const OPS_HIGHLIGHT_TERMS = [
  'GPS data',
  'fuel events',
  'movement events',
  'missing data',
  'at risk',
  'movements',
  'movement',
  'activity',
  'signals',
  'signal',
  'deliveries',
  'delivery',
  'telemetry',
  'anomalies',
  'anomaly',
  'observations',
  'observation',
  'mileage',
  'timestamps',
  'stops',
  'stop',
  'driver',
  'loading',
  'unloading',
  'pickups',
  'pickup',
  'schedule',
  'delays',
  'delay',
  'routes',
  'route',
  'depot',
  'hubs',
  'hub',
  'orders',
  'order',
  'GPS',
  'fuel',
  'revenue'
];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const OPS_HIGHLIGHT_RE = (() => {
  const sorted = [...OPS_HIGHLIGHT_TERMS].sort((a, b) => b.length - a.length);
  return new RegExp(`(${sorted.map((t) => escapeRegExp(t)).join('|')})`, 'gi');
})();

const OPS_HIGHLIGHT_LOOKUP = new Set(OPS_HIGHLIGHT_TERMS.map((t) => t.toLowerCase()));

function highlightOperationalTerms(segment: string, keyPrefix: string): ReactNode[] {
  if (!segment) return [];
  return segment.split(OPS_HIGHLIGHT_RE).flatMap((chunk, j) => {
    if (!chunk) return [];
    const hit = OPS_HIGHLIGHT_LOOKUP.has(chunk.toLowerCase());
    if (hit) {
      return [
        <span key={`${keyPrefix}-k-${j}`} className="font-semibold text-warning/95">
          {chunk}
        </span>,
      ];
    }
    return [<span key={`${keyPrefix}-t-${j}`}>{chunk}</span>];
  });
}

const EVIDENCE_CHIP_BASE =
  'inline-flex items-center rounded-md px-1.5 py-0.5 mx-0.5 align-baseline text-xs font-medium tabular-nums bg-secondary/95 text-destructive ring-1 ring-inset ring-accent/70';

/** Inline evidence index chips + keyword emphasis (parses [n] in text; chips show n only). */
function renderWhatHappenedBulletLine(
  line: string,
  lineIndex: number,
  opts: { onEvidenceRefClick?: (oneBasedIndex: number) => void; evidenceCount: number },
): ReactNode {
  const { onEvidenceRefClick, evidenceCount } = opts;
  const parts = line.split(/(\[\d+\])/);
  const out: ReactNode[] = [];
  parts.forEach((part, j) => {
    if (part === '') return;
    if (/^\[\d+\]$/.test(part)) {
      const n = parseInt(part.slice(1, -1), 10);
      const inRange = Number.isFinite(n) && n >= 1 && n <= evidenceCount;
      const interactive = Boolean(onEvidenceRefClick && inRange);
      if (interactive) {
        out.push(
          <Button
            type="button"
            key={`${lineIndex}-ref-${j}`}
            variant="secondary"
            size="sm"
            className={`${EVIDENCE_CHIP_BASE} h-auto align-baseline cursor-pointer hover:bg-accent/90 hover:text-destructive focus-visible:ring-ring/70 focus-visible:ring-offset-1`}
            onClick={() => onEvidenceRefClick?.(n)}
            title={`View evidence ${n} in panel`}
          >
            {n}
          </Button>,
        );
      } else {
        out.push(
          <span key={`${lineIndex}-ref-${j}`} className={EVIDENCE_CHIP_BASE}>
            {n}
          </span>,
        );
      }
    } else {
      out.push(...highlightOperationalTerms(part, `${lineIndex}-seg-${j}`));
    }
  });
  return <>{out}</>;
}

type ChecklistBlock = 'none' | 'reassuring' | 'items';

function resolveChecklistBlock(
  isAnomaly: boolean,
  isRisk: boolean,
  data: InvestigationResponse,
): ChecklistBlock {
  if (!isAnomaly && !isRisk) return 'none';
  const evidenceList = data.evidence || [];
  const combined = `${data.summary || ''} ${coerceInvestigatorMultilineField(data.detailed_explanation as unknown)}`.toLowerCase();
  const readsAllClear =
    /no anomalies?|nothing to worry|all clear|running normally|did not find|none were detected|no deliveries at risk|looks? normal from this check|no action required|nothing abnormal|no active anomaly/i.test(
      combined,
    );

  const anomalyEvidence = evidenceList.filter((e) => String(e?.evidence_type || '').startsWith('anomaly_'));
  const riskEvidence = evidenceList.filter((e) => e?.evidence_type === 'delivery_risk_flag');

  if (isAnomaly) {
    if (anomalyEvidence.length > 0) return 'items';
    if (readsAllClear) return 'reassuring';
    return 'items';
  }
  if (isRisk) {
    if (riskEvidence.length > 0) return 'items';
    if (readsAllClear) return 'reassuring';
    return 'items';
  }
  return 'none';
}

const ANOMALY_CHECKLIST = [
  'Check whether the vehicle is active today (shift / route).',
  'If telemetry is missing: confirm device power + connectivity.',
  'If movement looks impossible: verify timestamps and location source.',
  'Escalate the top 1–2 items to dispatch for immediate triage.',
];

const RISK_CHECKLIST = [
  'Review the affected orders and the latest status timestamps.',
  'Contact the customer or driver if the ETA is at risk.',
  'Check whether missing observations are causing low confidence.',
  'Prioritise the highest-risk items first.',
];

interface AssistantAnswerCardProps {
  data: InvestigationResponse;
  /** 1-based evidence index (answer text may use [n]); opens Evidence panel when wired from parent */
  onEvidenceRefClick?: (oneBasedIndex: number) => void;
  /** Called once the streamed summary prose has finished typing. Lets the
   *  parent defer rendering structural panels (Evidence, Timeline, follow-ups)
   *  until the prose has settled — see the "stream the prose, snap in the
   *  components" pattern. */
  onStreamingComplete?: () => void;
  /** Show the answer settled, without the typewriter — for an answer already
   *  revealed on another surface (pop-up ↔ full ZOE page). */
  disableStreaming?: boolean;
  activeProfitDrilldownId?: string | null;
  onProfitDrilldown?: (d: ProfitAgentDrilldown) => void;
  onOpenProfitabilityReport?: (d: ProfitAgentDrilldown) => void;
}

function profitDelegationToQueryResponse(
  delegation: ProfitDelegationPayload,
  question: string,
): ProfitAgentQueryResponse {
  return {
    ok: delegation.ok,
    question: delegation.question || question,
    context: {
      date_from: delegation.context.date_from,
      date_to: delegation.context.date_to,
      organisation_name: delegation.context.organisation_name ?? undefined,
      margin_threshold_pct: delegation.context.margin_threshold_pct,
      period_perspective: delegation.context.period_perspective,
    },
    summary: delegation.summary,
    bullets: delegation.bullets ?? [],
    recommended_actions: delegation.recommended_actions ?? [],
    drilldowns: delegation.drilldowns,
    data_quality_notes: delegation.data_quality_notes,
    tools_used: delegation.tools_used,
  };
}

export default function AssistantAnswerCard({
  data,
  onEvidenceRefClick,
  onStreamingComplete,
  disableStreaming = false,
  activeProfitDrilldownId,
  onProfitDrilldown,
  onOpenProfitabilityReport,
}: AssistantAnswerCardProps) {
  const profitDelegation = data.profit_delegation;
  const profitResult = useMemo(
    () => (profitDelegation ? profitDelegationToQueryResponse(profitDelegation, data.question) : null),
    [profitDelegation, data.question],
  );

  const isOutOfScope = data.intent === 'unsupported';
  const isCapabilitiesHelp = data.intent === 'capabilities_help';
  const intent = String(data.intent || '').toLowerCase();
  const isAnomaly = intent.includes('anomal') || intent.includes('operations_anomaly');
  const isRisk = intent.includes('risk');

  // ── Hooks: keep all hooks above any early return to satisfy Rules of Hooks.
  const opsBriefBlock = useMemo((): OpsBriefBlock | null => {
    const blocks = data.presentation?.blocks;
    if (!blocks?.length) return null;
    const found = blocks.find((b) => b.type === 'ops_brief');
    return found && found.type === 'ops_brief' ? found : null;
  }, [data.presentation?.blocks]);

  // `narrator` mode (per-org, §10.8): the LLM narrator's answer is the primary
  // line and `summary_line` drops to a supporting detail row. Default / older
  // servers: the deterministic `summary_line` leads, exactly as before.
  const answerSource = data.presentation?.answer_source ?? 'summary_line';

  const narratorAnswer = data.presentation?.narrative?.answer;

  const summaryText = useMemo(() => {
    if (answerSource === 'narrator') {
      const primary = (narratorAnswer || data.summary || '').trim();
      if (primary) return humanizeInvestigationDateTimeText(cleanSummaryText(primary));
    }
    if (opsBriefBlock?.summary_line) {
      return humanizeInvestigationDateTimeText(opsBriefBlock.summary_line);
    }
    return humanizeInvestigationDateTimeText(cleanSummaryText(data.summary || ''));
  }, [answerSource, narratorAnswer, data.summary, opsBriefBlock?.summary_line]);

  const prefersReducedMotion = usePrefersReducedMotion();
  // Out-of-scope card has its own fixed copy and doesn't stream; for everything
  // else we typewrite the summary so structural panels can "snap in" after.
  const streamingEnabled = !prefersReducedMotion && !isOutOfScope && !disableStreaming;
  const { displayText: typedSummary, isComplete: summaryComplete } = useTypewriter(summaryText, {
    charDelayMs: 14,
    enabled: streamingEnabled,
  });

  // Notify the parent the first time streaming completes for this investigation
  // so it can defer rendering Evidence / Timeline / FollowUps until then.
  const notifiedForIdRef = useRef<string | null>(null);
  useEffect(() => {
    notifiedForIdRef.current = null;
  }, [data.investigation_id]);
  useEffect(() => {
    if (!summaryComplete) return;
    if (notifiedForIdRef.current === data.investigation_id) return;
    notifiedForIdRef.current = data.investigation_id;
    onStreamingComplete?.();
  }, [summaryComplete, data.investigation_id, onStreamingComplete]);

  const explanationBlocks = useMemo(() => {
    const raw = coerceInvestigatorMultilineField(data.detailed_explanation as unknown);
    const summaryNorm = (data.summary || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return parseExplanationBlocks(raw)
      .filter((b) => {
        // Drop the LLM's own "Summary"/"Conclusion" subsection - we already show it at the top.
        const h = normalizeHeading(b.heading || '');
        return !REDUNDANT_SUMMARY_HEADINGS.has(h);
      })
      .map((b) => ({
        heading: b.heading,
        bullets: b.bullets
          .map((line) => humanizeInvestigationDateTimeText(line))
          .filter((line) => {
            // Belt-and-braces dedupe against the headline summary.
            if (!summaryNorm) return true;
            const bn = line.toLowerCase().replace(/\s+/g, ' ').trim();
            if (bn === summaryNorm) return false;
            if (summaryNorm.length > 40 && bn.includes(summaryNorm)) return false;
            return true;
          }),
      }))
      .filter((b) => b.bullets.length > 0);
  }, [data.detailed_explanation, data.summary]);

  // ── Early returns ─────────────────────────────────────────────────────────
  if (profitResult && (data.evidence?.length ?? 0) === 0) {
    const customerProfitQuestion = isCustomerProfitabilityQuestion(data.question);
    const periodOnlyDrilldown: ProfitAgentDrilldown = customerProfitQuestion
      ? { type: 'customer', id: '', label: 'Customer profitability' }
      : { type: 'vehicle', id: '', label: 'Full report' };
    return (
      <div className="space-y-3">
        <ProfitAnswerCard
          result={profitResult}
          activeDrilldownId={activeProfitDrilldownId}
          onDrilldown={onProfitDrilldown}
          onStreamingComplete={onStreamingComplete}
          disableStreaming={disableStreaming}
        />
        {onOpenProfitabilityReport && profitDelegation ? (
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={() => onOpenProfitabilityReport(periodOnlyDrilldown)}
            // amber-950/900/600 family kept raw: the single-valued `warning`
            // token cannot express these dark tints without a visible change
            className="px-3 py-2 rounded-lg text-amber-200 bg-amber-950/30 border border-amber-600/40 hover:bg-amber-900/40"
          >
            <ExternalLink className="w-4 h-4" />
            Open full Profitability Report
          </Button>
        ) : null}
        {profitDelegation?.data_plane ? (
          <p className="text-xs text-muted-foreground">
            Data source:{' '}
            <span className="text-foreground">
              {profitDelegation.data_plane === 'truth' ? 'Truth layer (v2)' : 'Legacy batch'}
            </span>
          </p>
        ) : null}
        {(profitDelegation?.data_quality_notes ?? []).length > 0 ? (
          // amber-950/700/100 family kept raw (see the profit-button note above).
          <div className="bg-amber-950/20 border border-amber-700/40 rounded-lg px-4 py-3 text-sm text-amber-100/90">
            <div className="font-medium text-amber-200 mb-1">Data notes</div>
            <ul className="list-disc list-inside space-y-1 text-amber-100/80">
              {profitDelegation!.data_quality_notes!.map((n, i) => (
                <li key={i}>{n.message}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  if (isOutOfScope) {
    return (
      <div className="bg-card/40 rounded-xl border border-border/50 p-4">
        <div className="flex items-start gap-3">
          {/* amber-900/700 family kept raw (see the profit-button note above). */}
          <div className="p-2 rounded-lg bg-amber-900/20 border border-amber-700/50">
            <Ban className="w-5 h-5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold text-foreground">Outside operational scope</div>
            <div className="text-sm text-muted-foreground mt-1">
              This assistant is designed for logistics operations. Try asking about an order, vehicle, driver, delivery risk, or anomalies.
            </div>
            {data.follow_ups?.length ? (
              <div className="mt-3 text-sm text-foreground">
                <div className={LABEL}>Suggested next questions</div>
                <ul className="list-disc pl-5 space-y-1">
                  {data.follow_ups.slice(0, 3).map((q, i) => (
                    <li key={i} className="text-foreground">
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // Meta-help ("What can Zoe do?"): summary-only card. No "What happened",
  // no checklist, no Evidence, no Timeline — the parent skips those too.
  if (isCapabilitiesHelp) {
    return (
      <div className="bg-card/40 rounded-xl border border-border/50 p-4">
        <div className="text-base text-foreground leading-snug">
          {typedSummary}
          {!summaryComplete ? <span className="zoe-stream-caret" aria-hidden /> : null}
        </div>
      </div>
    );
  }

  const checklistBlock = resolveChecklistBlock(isAnomaly, isRisk, data);
  const checklistItems = isAnomaly ? ANOMALY_CHECKLIST : isRisk ? RISK_CHECKLIST : [];
  const evidenceCount = data.evidence?.length ?? 0;
  const bulletRenderOpts = { onEvidenceRefClick, evidenceCount };
  const bodySectionCount = opsBriefBlock
    ? opsBriefBlock.sections.length
    : explanationBlocks.length;

  // Once `summaryComplete` flips true, each gated block mounts and plays the
  // `zoe-snap-in` animation — small per-block stagger gives the "build one by
  // one" feel without making the user wait between bullets.
  const SNAP_STAGGER_MS = 80;

  return (
    <div className="bg-card/40 rounded-xl border border-border/50 p-4 space-y-3">
      <div>
        <div className="text-base font-semibold text-foreground leading-snug whitespace-pre-line">
          {typedSummary}
          {!summaryComplete ? <span className="zoe-stream-caret" aria-hidden /> : null}
        </div>
      </div>

      {summaryComplete ? (
        opsBriefBlock ? (
          <OpsBriefPanel
            block={opsBriefBlock}
            staggerMs={SNAP_STAGGER_MS}
            // In `narrator` mode the primary line above is the narrator answer,
            // so keep the deterministic status line visible here as supporting
            // detail. In `summary_line` mode it is already the primary line, so
            // hide the duplicate.
            hideSummarySection={answerSource === 'summary_line' && Boolean(opsBriefBlock.summary_line)}
            // T115 — only what answers the question is drawn here; the summary
            // strip and the rest are under "More detail" (ZoeTurnCard).
            part="answer"
          />
        ) : explanationBlocks.length === 0 ? (
          <div className="bg-background/30 border border-border/40 rounded-lg p-3 zoe-snap-in">
            <div className={LABEL}>What happened</div>
            <p className="text-sm text-muted-foreground">—</p>
          </div>
        ) : (
          explanationBlocks.map((block, blockIdx) => {
            const sectionLabel =
              block.heading || (explanationBlocks.length === 1 ? 'What happened' : 'Details');
            return (
              <div
                key={blockIdx}
                className="bg-background/30 border border-border/40 rounded-lg p-3 zoe-snap-in"
                style={{ animationDelay: `${blockIdx * SNAP_STAGGER_MS}ms` }}
              >
                <div className={LABEL}>{sectionLabel}</div>
                <ul className="mt-1 list-disc pl-5 space-y-2.5 text-sm text-foreground marker:text-muted-foreground leading-relaxed">
                  {block.bullets.map((line, i) => (
                    <li key={i} className="pl-0.5 [&::marker]:text-muted-foreground">
                      {renderWhatHappenedBulletLine(line, i, bulletRenderOpts)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )
      ) : null}

      {summaryComplete && checklistBlock === 'reassuring' ? (
        <div
          className="bg-background/30 border border-border/40 rounded-lg p-3 zoe-snap-in"
          style={{ animationDelay: `${bodySectionCount * SNAP_STAGGER_MS}ms` }}
        >
          <div className={LABEL}>From this check</div>
          <p className="text-sm text-foreground leading-relaxed">
            No action required from this snapshot — nothing flagged that needs an immediate ops response.
          </p>
        </div>
      ) : null}

      {summaryComplete && checklistBlock === 'items' && checklistItems.length > 0 ? (
        <div
          className="bg-background/30 border border-border/40 rounded-lg p-3 zoe-snap-in"
          style={{ animationDelay: `${bodySectionCount * SNAP_STAGGER_MS}ms` }}
        >
          <div className={LABEL}>If you need to act</div>
          <ul className="text-sm text-foreground space-y-1">
            {checklistItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0" />
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {summaryComplete && profitResult && (data.evidence?.length ?? 0) > 0 ? (
        <div className="pt-2 border-t border-border/50 zoe-snap-in">
          <div className="text-sm font-medium text-warning/90 mb-2">Profit analysis</div>
          <ProfitAnswerCard
            result={profitResult}
            activeDrilldownId={activeProfitDrilldownId}
            onDrilldown={onProfitDrilldown}
            disableStreaming={disableStreaming}
          />
          {onOpenProfitabilityReport ? (
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => onOpenProfitabilityReport({ type: 'vehicle', id: '', label: 'Full report' })}
              // amber-950/900/600 family kept raw (see the profit-button note above).
              className="mt-3 px-3 py-2 rounded-lg text-amber-200 bg-amber-950/30 border border-amber-600/40 hover:bg-amber-900/40"
            >
              <ExternalLink className="w-4 h-4" />
              Open full Profitability Report
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
